/**
 * @file Cytoscape.js stylesheet defining visuals of diagram elements and edges.
 */

// 结构类边槽位辅助：同一节点下不同类型均分底部槽位，避免源端重叠
const structuralEdgeTypes = [
  'Aggregation-participation',
  'Exhibition-characterization',
  'Generalization-specialization',
  'Classification-instantiation',
];
const structuralTypeOrder = structuralEdgeTypes;
const STRUCT_SLOT_MIN_GAP = 22; // 槽位最小间距，节点过窄时保证可视分离
const INVOCATION_ZIGZAG_HALF = 20; // 调用折线中段的半长（沿连线方向）
const INVOCATION_ZIGZAG_OFFSET = 6; // 调用折线偏移（垂直于连线方向）

const getStructuralSlotOffset = (edge: any) => {
  const source = edge.source?.();
  if (!source || source.empty?.()) return 0;

  const type = edge.data?.('type');
  if (!structuralEdgeTypes.includes(type)) return 0;

  const outgoingStructural = source
    .connectedEdges?.()
    .filter(
      (e: any) =>
        e.source?.()?.id?.() === source.id?.() &&
        structuralEdgeTypes.includes(e.data?.('type'))
    );

  const typeSlots = structuralTypeOrder.filter(t =>
    outgoingStructural.some((e: any) => e.data?.('type') === t)
  );
  if (!typeSlots.length) return 0;

  const slotIndex = typeSlots.indexOf(type);
  if (slotIndex === -1) return 0;

  const width = source.outerWidth?.() ?? 0;
  const effectiveWidth = Math.max(
    width,
    (typeSlots.length + 1) * STRUCT_SLOT_MIN_GAP
  );

  // -50% 到 50% 均分映射，返回基于节点中心的 px 偏移
  return (
    -effectiveWidth / 2 +
    ((slotIndex + 1) * effectiveWidth) / (typeSlots.length + 1)
  );
};

// 结构边首段垂直下探距离
const getStructuralTaxiTurn = (edge: any, miniResultOffest: number = 0) => {
  const srcX = edge.source().position('x');
  const srcY = edge.source().position('y');
  const tgtX = edge.target().position('x');
  const tgtY = edge.target().position('y');
  const xOffset = Math.abs(srcX - tgtX);
  const yOffset = srcY - tgtY;
  // 只有当：1. 源在目标下方 2. X偏移不大（在目标正下方区域）时，才用大 taxi-turn 合并
  // 否则用小值实现短转折
  // const isDirectlyBelow = yOffset > -105 && xOffset < 1500;
  const isDirectlyBelow = yOffset > -105;
  // console.log('taxi-turn', yOffset, xOffset, isDirectlyBelow);
  return isDirectlyBelow ? 80 + miniResultOffest : 1000000;
};

const getInvocationSegmentWeights = (edge: any) => {
  const source = edge.source?.();
  const target = edge.target?.();
  if (!source || !target) return [0.45, 0.55];
  const srcX = source.position('x');
  const srcY = source.position('y');
  const tgtX = target.position('x');
  const tgtY = target.position('y');
  const length = Math.hypot(tgtX - srcX, tgtY - srcY);
  if (!length) return [0.45, 0.55];
  const delta = INVOCATION_ZIGZAG_HALF / length;
  // 调用折线先向目标方向前进，再回折形成明显的 Z 形
  return [Math.min(0.9, 0.5 + delta), Math.max(0.1, 0.5 - delta)];
};

const getInvocationSegmentDistances = (edge: any) => {
  const source = edge.source?.();
  const target = edge.target?.();
  if (!source || !target)
    return [-INVOCATION_ZIGZAG_OFFSET, INVOCATION_ZIGZAG_OFFSET];
  const srcX = source.position('x');
  const srcY = source.position('y');
  const tgtX = target.position('x');
  const tgtY = target.position('y');
  const length = Math.hypot(tgtX - srcX, tgtY - srcY);
  if (!length) return [-INVOCATION_ZIGZAG_OFFSET, INVOCATION_ZIGZAG_OFFSET];
  return [-INVOCATION_ZIGZAG_OFFSET, INVOCATION_ZIGZAG_OFFSET];
};

const isParentChildInvocationEdge = (edge: any) => {
  const source = edge.source?.();
  const target = edge.target?.();
  if (!source || !target) return false;

  const sourceParent = source.parent?.();
  if (
    source.isChild?.() &&
    sourceParent?.length > 0 &&
    sourceParent[0].id?.() === target.id?.()
  ) {
    return true;
  }

  const targetParent = target.parent?.();
  if (
    target.isChild?.() &&
    targetParent?.length > 0 &&
    targetParent[0].id?.() === source.id?.()
  ) {
    return true;
  }

  return false;
};

const isChildToParentInvocationEdge = (edge: any) => {
  const source = edge.source?.();
  const target = edge.target?.();
  if (!source || !target) return false;
  const sourceParent = source.parent?.();
  return (
    source.isChild?.() &&
    sourceParent?.length > 0 &&
    sourceParent[0].id?.() === target.id?.()
  );
};

const isParentToChildInvocationEdge = (edge: any) => {
  const source = edge.source?.();
  const target = edge.target?.();
  if (!source || !target) return false;
  const targetParent = target.parent?.();
  return (
    target.isChild?.() &&
    targetParent?.length > 0 &&
    targetParent[0].id?.() === source.id?.()
  );
};

const getInvocationCurveStyle = (edge: any) =>
  isParentChildInvocationEdge(edge) ? 'taxi' : 'segments';

const getInvocationEdgeDistances = (edge: any) =>
  isParentChildInvocationEdge(edge) ? 'node-position' : 'intersection';

const getInvocationTaxiDirection = (edge: any) => {
  if (isChildToParentInvocationEdge(edge)) return 'upward';
  if (isParentToChildInvocationEdge(edge)) return 'downward';
  return 'auto';
};

const getInvocationSourceEndpoint = (edge: any) => {
  if (isParentToChildInvocationEdge(edge)) {
    return '0% -50%';
  }
  return 'outside-to-node';
};

const getInvocationTargetEndpoint = (edge: any) => {
  if (isChildToParentInvocationEdge(edge)) {
    return '0% -50%';
  }
  if (isParentToChildInvocationEdge(edge)) {
    return 'outside-to-node';
  }
  return 'outside-to-node';
};

export const cyStylesheet = [
  // node styling
  {
    selector: 'node',
    style: {
      content: 'data(label)',
      'border-width': '2px',
      'background-color': 'white',
      width: '70px',
      height: '45px',
      'text-valign': 'center',
      'taxi-direction': 'vertical',
      'ghost-offset-x': 3,
      'ghost-offset-y': 3,
      'ghost-opacity': 0.4,
      'font-family': '\"PingFang SC\", \"Microsoft YaHei\", Arial, sans-serif',
      'font-size': '14px',
      'font-weight': '500',
      'text-outline-width': 0,
      'text-outline-color': 'transparent',
      color: '#132347',
      'z-index': 10,
      'z-index-compare': 'manual',
    },
  },
  {
    selector: 'node.marker-label',
    style: {
      // 连线小标使用独立节点承载，避免被节点覆盖
      content: 'data(markerLabel)',
      width: 1,
      height: 1,
      padding: 0,
      'background-opacity': 0,
      'border-width': 0,
      'text-wrap': 'none',
      'text-background-opacity': 0,
      'text-outline-width': 0,
      'text-outline-opacity': 0,
      'font-family': '\"PingFang SC\", \"Microsoft YaHei\", Arial, sans-serif',
      'font-size': '18px',
      'font-weight': 'bold',
      color: 'data(markerColor)',
      events: 'no',
      'text-events': 'no',
      'z-index': 20,
      'z-index-compare': 'manual',
    },
  },

  {
    selector: 'node[displayLabel]',
    style: {
      content: 'data(displayLabel)',
    },
  },
  // 注意：Cytoscape selector 不支持 :not(...)，因此用 base node 的 content 作为 fallback

  {
    selector: 'node[essence]',
    style: {
      ghost: 'data(essence)',
    },
  },

  {
    selector: 'node[affiliation]',
    style: {
      'border-style': 'data(affiliation)',
    },
  },

  {
    selector: 'node[type = "object"]',
    style: {
      shape: 'rectangle',
      'border-color': '#70E483',
      padding: '5px',
      'min-width': 120,
    },
  },
  {
    selector: 'node[type = "process"]',
    style: {
      shape: 'ellipse',
      'border-color': '#3BC3FF',
      padding: '8px',
      'min-width': 120,
    },
  },
  {
    selector: 'node[type = "state"]',
    style: {
      shape: 'round-rectangle',
      'border-color': 'orange',
      'min-width': 50,
    },
  },
  {
    selector: 'node[type = "value"]',
    style: {
      shape: 'round-rectangle',
      'border-color': '#4169E1', // 皇家蓝色，区分于状态节点
      'min-width': 50,
    },
  },
  {
    selector: 'node[labelWidth]',
    style: {
      width: 'data(labelWidth)',
    },
  },
  //compound nodes styling (objects with states/values and in-zoomed processes)
  {
    selector: '$node > node[type = "state"], $node > node[type = "value"]',
    style: {
      'text-valign': 'center',
      padding: 8,
      'min-width': 80,
      'min-height': 60,
    },
  },
  {
    selector: '$node > node[type = "object"], $node > node[type = "process"]',
    style: {
      'text-valign': 'center',
      padding: 8,
      'min-width': 180,
      'min-height': 300,
    },
  },
  {
    selector: 'node:parent[type = "process"]',
    style: {
      'text-valign': 'top',
      'text-margin-y': 25, // 将标签向下移动到节点内部，与 object 父节点保持一致
      padding: 10,
      'min-width': 360,
      'min-height': 0,
    },
  },
  {
    selector: 'node:parent[type = "object"]',
    style: {
      'text-valign': 'top',
      'text-margin-y': 25, // 将标签向下移动到节点内部
      padding: 10,
      'min-height': 150, // 有状态时固定最小高度
    },
  },
  //edge styling
  {
    selector: 'edge',
    style: {
      'curve-style': 'bezier',
      'arrow-scale': 1.5,
      width: 2,
      'line-color': '#586D8C',
      'target-arrow-color': '#586D8C',
      'source-arrow-color': '#586D8C',
      'z-index-compare': 'manual',
      'z-index': 5,
    },
  },
  {
    selector: 'edge[label]',
    style: {
      content: 'data(label)',
      'text-background-opacity': 0.8,
      'text-background-color': 'white',
      'text-background-shape': 'round-rectangle',
      'text-background-padding': 5,
      'font-family': '\"PingFang SC\", \"Microsoft YaHei\", Arial, sans-serif',
      'font-size': '10px',
      'font-weight': '400',
      color: '#132347',
    },
  },
  {
    selector: 'edge[markerLabel]',
    style: {
      // 连线小标改用独立节点展示，避免整条线抬升 z-index
      'target-label': '',
      'target-text-rotation': 'none',
      'target-text-margin-y': '-18px',
      'target-text-margin-x': '0px',
      'font-size': '10px',
      'font-weight': '400',
      'text-background-opacity': 0,
      'text-outline-width': 0,
      'text-wrap': 'none',
      'text-outline-color': 'transparent',
      'text-outline-opacity': 0,
    },
  },
  {
    // 单向/双向关联的边标签增加半透明白底，提升文字可读性
    selector:
      'edge[type = "Tagged"][label], edge[type = "bidirectional-arrow"][label]',
    style: {
      'text-background-color': 'white',
      'text-background-opacity': 0.6,
    },
  },
  {
    selector: 'edge[type = "Consumption"]',
    style: {
      'target-arrow-shape': 'influence-arrow',
      'arrow-scale': 2.8, // 消耗关系箭头与影响关系保持一致
      'target-arrow-fill': 'hollow',
    },
  },

  {
    selector: 'edge[type = "Generation"]',
    style: {
      'target-arrow-shape': 'influence-arrow',
      'arrow-scale': 2.8, // 生成关系箭头与影响关系保持一致
      'target-arrow-fill': 'hollow',
    },
  },

  {
    selector: 'edge[type = "bidirectional-arrow"]',
    style: {
      // 对等关联（双向）使用与单向关联一致的箭头样式
      'source-arrow-shape': 'vee',
      'target-arrow-shape': 'vee',
    },
  },
  {
    selector: 'edge[type = "Effect"]',
    style: {
      'source-arrow-shape': 'influence-arrow',
      'target-arrow-shape': 'influence-arrow',
      'arrow-scale': 2.8, // 影响关系箭头稍微放大
      'source-arrow-fill': 'hollow', // 影响关系用空心双向箭头
      'target-arrow-fill': 'hollow', // 影响关系用空心双向箭头
    },
  },
  {
    selector: 'edge[type = "Invocation"]',
    style: {
      'curve-style': (edge: any) => getInvocationCurveStyle(edge),
      'edge-distances': (edge: any) => getInvocationEdgeDistances(edge),
      'segment-weights': (edge: any) => getInvocationSegmentWeights(edge), // 调用关系在中段做小幅折线
      'segment-distances': (edge: any) => getInvocationSegmentDistances(edge), // 调用关系在中段做小幅折线
      'taxi-direction': (edge: any) => getInvocationTaxiDirection(edge),
      'taxi-turn': (edge: any) => (isParentChildInvocationEdge(edge) ? 48 : 0),
      'taxi-turn-min-distance': (edge: any) =>
        isParentChildInvocationEdge(edge) ? '28px' : '0px',
      'source-endpoint': (edge: any) => getInvocationSourceEndpoint(edge),
      'target-endpoint': (edge: any) => getInvocationTargetEndpoint(edge),
      'z-index': (edge: any) => (isParentChildInvocationEdge(edge) ? 30 : 5),
      'z-compound-depth': (edge: any) =>
        isParentChildInvocationEdge(edge) ? 'top' : 'auto',
      'target-arrow-shape': 'influence-arrow',
      'arrow-scale': 2.8, // 调用关系箭头与影响关系保持一致
      'target-arrow-fill': 'hollow',
    },
  },
  {
    selector: `edge[type = "Aggregation-participation"],
              edge[type = "Exhibition-characterization"], 
              edge[type = "Generalization-specialization"], 
              edge[type = "Classification-instantiation"]`,
    style: {
      'curve-style': 'taxi',
      'taxi-direction': 'downward',
      'taxi-turn': (ele: any) => getStructuralTaxiTurn(ele, 0),
      'taxi-turn-min-distance': '18px',
      'source-arrow-shape': 'triangle',
      'arrow-scale': 3,
      'source-distance-from-node': '14px',
      // x 偏移分槽，y 固定在节点底部（100%），确保贴边且箭头稍下移
      'source-endpoint': (ele: any) => `${getStructuralSlotOffset(ele)}px 50%`,
      'target-endpoint': 'outside-to-node',
    },
  },
  {
    selector: 'edge[type = "Exhibition-characterization"]',
    style: {
      'source-arrow-shape': 'triangle-inner-triangle',
      'taxi-turn': (ele: any) => getStructuralTaxiTurn(ele, 10),
      'source-arrow-fill': 'hollow',
    },
  },
  {
    selector: 'edge[type = "Generalization-specialization"]',
    style: {
      'source-arrow-fill': 'hollow',
      'taxi-turn': (ele: any) => getStructuralTaxiTurn(ele, 20),
    },
  },
  {
    selector: 'edge[type = "Classification-instantiation"]',
    style: {
      'source-arrow-shape': 'triangle-inner-circle',
      'source-arrow-fill': 'hollow',
      'taxi-turn': (ele: any) => getStructuralTaxiTurn(ele, 30),
    },
  },
  {
    selector: 'edge[type = "Instrument"]',
    style: {
      'target-arrow-shape': 'circle',
      'target-arrow-fill': 'hollow',
    },
  },
  {
    selector: 'edge[type = "Agent"]',
    style: {
      'target-arrow-shape': 'circle',
    },
  },
  {
    selector: 'edge[type = "Tagged"]',
    style: {
      'target-arrow-shape': 'vee',
    },
  },
  {
    selector: 'edge.eh-ghost-edge',
    style: {
      'target-arrow-shape': 'triangle',
    },
  },
  {
    selector: 'node[display], edge[display]',
    style: {
      display: 'data(display)',
    },
  },
  //derived edges
  {
    selector: 'edge[MMRef.originalEdges.length > 0]',
    style: {
      'line-color': '#ccc',
      'target-arrow-color': '#ccc',
    },
  },
  // cytoscape-edge-handles extention styling, changes in styles while creating an edge and hovering over elements
  {
    selector: '.eh-source',
    style: {
      'border-width': 2,
    },
  },
  {
    selector: '.eh-hover',
    style: {
      'border-width': 3,
      'background-color': '#e8e8e8',
      'background-opacity': 0.3,
    },
  },
  {
    selector: '.eh-preview, .eh-ghost-edge',
    style: {
      'background-color': 'grey',
      'line-color': 'grey',
      'target-arrow-color': 'grey',
      'source-arrow-color': 'grey',
    },
  },
  // 节点选中状态样式
  {
    selector: 'node:selected',
    style: {
      'border-width': 3,
      'border-color': '#4a90d9',
      'overlay-padding': 4,
      'overlay-color': '#4a90d9',
      'overlay-opacity': 0.1,
    },
  },
  // 框选选中状态样式
  {
    selector: 'node.box-selected',
    style: {
      'border-width': 2,
      'border-color': '#1890ff',
      'border-style': 'dashed',
      'overlay-padding': 3,
      'overlay-color': '#1890ff',
      'overlay-opacity': 0.15,
    },
  },
  {
    selector: 'node[hasSubdiagram = 1]',
    style: {
      'border-width': 4,
    },
  },
  // 连线模式源节点样式
  {
    selector: 'node.connecting-source',
    style: {
      'border-width': 3,
      'border-color': '#52c41a',
    },
  },
  // 连线模式目标节点悬停样式
  {
    selector: 'node.connecting-target',
    style: {
      'border-width': 3,
      'border-color': '#faad14',
      'background-color': '#fffbe6',
    },
  },
  // 自定义宽高支持
  {
    selector: 'node[customWidth]',
    style: {
      width: 'data(customWidth)',
    },
  },
  {
    selector: 'node[customHeight]',
    style: {
      height: 'data(customHeight)',
    },
  },
  // 父节点（compound node）自定义尺寸支持
  // 父节点的 width/height 是根据子节点自动计算的，需要用 min-width/min-height 来控制
  {
    selector: 'node:parent[customWidth]',
    style: {
      'min-width': 'data(customWidth)',
    },
  },
  {
    selector: 'node:parent[customHeight]',
    style: {
      'min-height': 'data(customHeight)',
    },
  },
];
